import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, formatINR } from "@/lib/payments";

const view = (s) => {
  if (!s) return null;
  if (s.quotation_status === "paid") return { Icon: CheckCircle2, color: "text-green-600", title: "Payment received", text: "Thank you. Our team will be in touch to confirm dispatch details.", key: "success" };
  if (s.quotation_status === "payment_review") return { Icon: Clock, color: "text-amber-600", title: "Payment under review", text: "We received your payment and are verifying it. No further action is needed.", key: "review" };
  if (s.quotation_status === "cancelled") return { Icon: XCircle, color: "text-stone-500", title: "Quotation cancelled", text: "This quotation is no longer payable. Please contact Prithira Global.", key: "cancelled" };
  if (!s.payment_state) return { Icon: Clock, color: "text-stone-500", title: "No payment started yet", text: "You haven't started a payment for this quotation.", key: "not-started" };
  if (["FAILED", "CREATE_FAILED"].includes(s.payment_state)) return { Icon: XCircle, color: "text-red-600", title: "Payment not completed", text: "No money was taken, or any debit will be reversed by your bank. You can try again.", key: "failed" };
  return { Icon: Clock, color: "text-amber-600", title: "Confirming your payment…", text: "This usually takes a few seconds. Please keep this page open.", key: "pending" };
};

export default function PaymentStatusPage() {
  const { token } = useParams();
  const [s, setS] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    let tries = 0;
    let timer;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/pay/${token}/status`);
        if (!alive) return;
        setS(r.data);
        const done = ["paid", "payment_review", "cancelled"].includes(r.data.quotation_status) || !r.data.payment_state || ["FAILED", "CREATE_FAILED"].includes(r.data.payment_state);
        if (!done && ++tries < 24) timer = setTimeout(check, 5000);
      } catch {
        if (alive) setError(true);
      }
    };
    check();
    return () => { alive = false; clearTimeout(timer); };
  }, [token]);

  const v = view(s);
  return (
    <section className="bg-stone-50 min-h-[70vh] pt-32 md:pt-40 lg:pt-48 pb-16 px-4" data-testid="payment-status-page">
      <Helmet title="Payment status | Prithira Global" meta={[{ name: "robots", content: "noindex, nofollow" }]} />
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-stone-200 p-10 text-center space-y-4">
        {error ? <p className="text-stone-600" data-testid="payment-status-error">This payment link is invalid.</p>
          : !v ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          : (
            <>
              <v.Icon className={`h-14 w-14 mx-auto ${v.color}`} />
              <h1 className="font-heading text-3xl text-stone-900" data-testid={`payment-status-${v.key}`}>{v.title}</h1>
              <p className="text-stone-600">{v.text}</p>
              <p className="text-sm text-stone-500" data-testid="payment-status-summary">{s.number} • {formatINR(s.amount_paise)}</p>
              {["failed", "not-started"].includes(v.key) && s.quotation_status === "approved" && (
                <Button asChild className="rounded-full" data-testid="payment-retry-btn"><Link to={`/pay/${token}`}>{v.key === "failed" ? "Try again" : "View quotation"}</Link></Button>
              )}
            </>
          )}
      </div>
    </section>
  );
}
